/**
 * ParlayCards
 * Injury report, live parlay list, and single parlay detail cards.
 */

import type {
  InjuryEntry,
  InjuryReportCardData,
  LiveParlayCardData,
  TrackedParlayCard,
} from '@parlay-picker/shared/chatTypes';
import { leagueKeyOrNull } from '@parlay-picker/shared/league';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { findTeam } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import { AlertTriangle, BookOpen, Check, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { AvatarWithBadge, ResultBadge } from './cardHelpers';

// =============================================================================
// Injury Report Card
// =============================================================================

export function InjuryReportCard({ data }: { data: InjuryReportCardData }): ReactElement {
  const team = data.teamAbbr || 'Unknown Team';
  const injuries = data.injuries;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-theme-danger" />
        <span className="text-sm font-semibold text-theme-text">{team} Injuries</span>
        {injuries && (
          <span className="text-xs text-theme-text-tertiary ml-auto">{injuries.length}</span>
        )}
      </div>
      {injuries && injuries.length > 0 ? (
        <div className="space-y-1.5">
          {injuries.map((inj, i) => (
            <InjuryRow key={i} injury={inj} teamAbbr={team} />
          ))}
        </div>
      ) : (
        <div className="text-xs text-theme-success">No injuries reported</div>
      )}
    </div>
  );
}

function InjuryRow({ injury, teamAbbr }: { injury: InjuryEntry; teamAbbr: string }): ReactElement {
  const playerName = injury.playerName || 'Unknown';
  const injTeamAbbr = injury.teamAbbr || teamAbbr;
  const injuryLeagueKey = leagueKeyOrNull(injury.leagueKey);
  const headshotUrl = usePlayerHeadshotUrl(playerName, injuryLeagueKey);
  const teamInfo = findTeam(injTeamAbbr, injuryLeagueKey);
  const status = injury.status || 'Unknown';
  const injuryDesc = injury.injury ?? null;

  return (
    <div className="flex items-start gap-2 min-w-0">
      <AvatarWithBadge headshotUrl={headshotUrl} teamLogo={teamInfo?.logo} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 justify-between">
          <span className="text-xs text-theme-text font-medium truncate">{playerName}</span>
          <span
            className={`text-xs font-medium shrink-0 ${
              status.toLowerCase() === 'out'
                ? 'text-theme-danger'
                : status.toLowerCase() === 'doubtful'
                  ? 'text-theme-danger/70'
                  : 'text-theme-warning'
            }`}
          >
            {status}
          </span>
        </div>
        {injuryDesc && (
          <span className="text-[10px] text-theme-text-tertiary leading-tight">{injuryDesc}</span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Live Parlay Card (tracked parlays list + single parlay detail)
// =============================================================================

export function LiveParlayCard({ data }: { data: LiveParlayCardData }): ReactElement {
  // Single parlay detail
  if (data.parlay) {
    return <SingleParlayCard parlay={data.parlay} />;
  }

  // List of parlays
  const parlays = data.parlays;
  if (!parlays || parlays.length === 0) {
    return (
      <div className="bg-theme-surface border border-theme-border rounded-xl p-3">
        <div className="text-xs text-theme-text-secondary">No parlays found</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {parlays.map((parlay, i) => (
        <SingleParlayCard key={i} parlay={parlay} compact />
      ))}
    </div>
  );
}

export function SingleParlayCard({
  parlay,
  compact,
}: {
  parlay: TrackedParlayCard;
  compact?: boolean;
}): ReactElement {
  const id = parlay.id;
  const result = parlay.result || 'pending';
  const legCount = parlay.legCount || 0;
  const stake = parlay.stake ? `$${parlay.stake}` : null;
  const totalOdds = parlay.totalOdds || null;
  const name = parlay.name || `Parlay #${id || '?'}`;
  const legs = parlay.legs;
  const sportsbook = parlay.sportsbook || null;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="w-4 h-4 text-theme-primary shrink-0" />
          <span className="text-sm font-semibold text-theme-text truncate">{name}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {totalOdds && <span className="text-xs text-theme-text-secondary">{totalOdds}</span>}
          <ResultBadge result={result} />
        </div>
      </div>

      {/* Metadata row */}
      <div className="flex items-center gap-3 text-[10px] text-theme-text-tertiary">
        <span>{legCount} legs</span>
        {stake && <span>{stake}</span>}
        {sportsbook && <span className="capitalize">{sportsbook}</span>}
      </div>

      {/* Legs (show in detail mode, first 4 in compact) */}
      {legs && legs.length > 0 && (
        <div className="space-y-1 border-t border-theme-border/50 pt-1.5">
          {(compact ? legs.slice(0, 4) : legs).map((leg, j) => {
            const hit = leg.hit;
            return (
              <div key={j} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                  {hit != null && (
                    <span
                      className={`shrink-0 ${hit ? 'text-theme-success' : 'text-theme-danger'}`}
                    >
                      {hit ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    </span>
                  )}
                  <span className="text-theme-text font-medium truncate">
                    {String(leg.player || '?')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 text-theme-text-secondary">
                  <span>
                    {String(leg.overUnder || '')} {leg.line != null ? String(leg.line) : ''}{' '}
                    {formatStatLabel(String(leg.statType || leg.prop || ''))}
                  </span>
                  {leg.actualValue != null && (
                    <span className="text-theme-text-tertiary">({String(leg.actualValue)})</span>
                  )}
                </div>
              </div>
            );
          })}
          {compact && legs.length > 4 && (
            <div className="text-[10px] text-theme-text-tertiary">+{legs.length - 4} more legs</div>
          )}
        </div>
      )}
    </div>
  );
}
